/**
 * Enhanced column factories that use dedicated cell renderer components
 * These provide better performance and more features than the basic factories
 */

import { h } from 'vue';
import type { ColumnDef, Row } from '@tanstack/vue-table';
import type { ColumnOptions, TagVariant } from '../types';
import TagComponent from '../../ui/tag/TagComponent.vue';
import AvatarNameCell from '../cells/AvatarNameCell.vue';
import EmailLinkCell from '../cells/EmailLinkCell.vue';
import DateTimeCell from '../cells/DateTimeCell.vue';
import ActionButtonsCell from '../cells/ActionButtonsCell.vue';
import ResponseTimeCell from '../cells/ResponseTimeCell.vue';
import PriorityCell from '../cells/PriorityCell.vue';
import TextCell from '../cells/TextCell.vue';
import CountCell from '../cells/CountCell.vue';
import PhoneCell from '../cells/PhoneCell.vue';
import ServiceLevelCell from '../cells/ServiceLevelCell.vue';
import OrganizationNameCell from '../cells/OrganizationNameCell.vue';
import UserRoleTagComponent from '@/components/ui/tag/UserRoleTagComponent.vue';
import SecurityBriefedCell from '../cells/SecurityBriefedCell.vue';
import type { CellContext } from '@tanstack/vue-table';

/**
 * Creates a tag column using TagComponent component
 */
export function createTagColumn<T>(
    accessor: keyof T,
    header: string,
    options?: ColumnOptions & {
        variant?: TagVariant; //'default' | 'success' | 'warning' | 'danger' | 'info' | 'hold';
        customVariants?: Record<string, TagVariant>;
    },
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        accessorFn: typeof accessor === 'function' ? accessor : undefined,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(TagComponent, {
                value: info.getValue() as string,
                variant: options?.variant,
                customVariants: options?.customVariants,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

/**
 * Creates a status tag column using TagComponent component
 */
export function createStatusColumn<T>(
    accessor: keyof T,
    header: string = 'Status',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        // accessorFn:
        accessorFn: typeof accessor === 'function' ? accessor : undefined,

        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(TagComponent, {
                value: info.getValue() as string,
                showDot: true,
                // variant: options?.variant,
                // customVariants: options?.customVariants,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

export function createResponseTimeColumn<T>(
    accessor: keyof T,
    header: string = 'Response Time',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        accessorFn: typeof accessor === 'function' ? accessor : undefined,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(ResponseTimeCell, {
                responseTime: info.getValue() as number | null,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

export function createPriorityColumn<T>(
    accessor: keyof T,
    header: string = 'Priority',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        accessorFn: typeof accessor === 'function' ? accessor : undefined,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(PriorityCell, {
                priority: info.getValue() as string | null,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

/**
 * Creates a name with avatar column using AvatarNameCell component
 */
export function createNameWithAvatarColumn<T>(
    firstNameAccessor: keyof T,
    lastNameAccessor: keyof T,
    header: string = 'Name',
    options?: ColumnOptions & { size?: 'x-small' | 'small' | 'medium' | 'large' },
): ColumnDef<T, unknown> {
    return {
        accessorKey: firstNameAccessor as string,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            const firstName = info.getValue() as string;
            const lastName = info.row.original[lastNameAccessor] as string;

            return h(AvatarNameCell, {
                firstName: firstName,
                lastName: lastName,
                size: options?.size || 'x-small',
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
            cellClass: options?.className,
        },
    };
}

/**
 * Creates an email column using EmailLinkCell component
 */
export function createEmailColumn<T>(
    accessor: keyof T,
    header: string = 'Email',
    options?: ColumnOptions & { maxLength?: number },
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(EmailLinkCell, {
                email: info.getValue() as string | null,
                maxLength: options?.maxLength,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

/**
 * Creates a date column using DateTimeCell component
 */
export function createDateColumn<T>(
    accessor: keyof T,
    header: string,
    options?: ColumnOptions & {
        format?: 'date' | 'datetime' | 'time';
        emptyText?: string;
    },
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(DateTimeCell, {
                date: info.getValue() as Date | null,
                format: options?.format || 'date',
                emptyText: options?.emptyText,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
            cellClass: options?.className,
        },
    };
}

/**
 * Creates a date with time column using DateTimeCell component
 */
export function createDateTimeColumn<T>(
    accessor: keyof T,
    header: string,
    options?: ColumnOptions & { emptyText?: string },
): ColumnDef<T, unknown> {
    return createDateColumn(accessor, header, {
        ...options,
        format: 'datetime',
    });
}

/**
 * Creates an actions column using ActionButtonsCell component
 */
export function createActionsColumn<T>(
    getActions: (row: T) => Array<{
        label?: string;
        icon?: string;
        onClick: () => void;
        variant?: 'primary' | 'secondary' | 'danger' | 'link';
        disabled?: boolean;
    }>,
    header: string = '',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        id: 'actions',
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(ActionButtonsCell, {
                actions: getActions(info.row.original),
            });
        },
        enableSorting: false,
        meta: {
            headerClass: options?.className,
            cellClass: options?.className || 'flex justify-end',
        },
    };
}

export function createContactTypeColumn<T>(accessor: keyof T, header: string = 'Type'): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        header: header ?? 'Type',
        cell: (info) => {
            const value = info.getValue() as string | null | undefined;
            const variant = value === 'Technical' ? 'hold' : value === 'Billing' ? 'success' : ('default' as const);

            return h(TagComponent, {
                value: value ?? '-',
                variant: variant,
            });
        },
        size: 300,
        enableSorting: true,
    };
}

/**
 * Creates a column that renders one tag per assigned contact type.
 * Used where a contact can hold multiple types (Primary/Billing/Technical)
 * and they should all appear on a single row (ITDEVELOP-1775).
 *
 * Only the types a contact actually holds are rendered, in a consistent order
 * — Primary, then Billing, then Technical — in a wrapping flex row so the tags
 * reflow on narrow widths.
 *
 * When `options.onRemove` is supplied, each tag gets a remove (X) button so a
 * single type can be removed on its own. The per-type assignment id is read
 * from the row field named by `options.idsAccessor`, which must hold a
 * `number[]` parallel to the displayed type names.
 */
const CONTACT_TYPE_ORDER = ['Primary', 'Billing', 'Technical'] as const;

export function createContactTypesColumn<T>(
    accessor: keyof T,
    header: string = 'Type',
    options?: {
        onRemove?: (row: T, contactType: string, organizationContactId: number) => void;
        idsAccessor?: keyof T;
    },
): ColumnDef<T, unknown> {
    const variantFor = (value: string) =>
        value === 'Technical' ? 'hold' : value === 'Billing' ? 'success' : ('default' as const);

    return {
        accessorKey: accessor as string,
        header: header ?? 'Type',
        cell: (info) => {
            const values = (info.getValue() ?? []) as string[];
            const ids = options?.idsAccessor
                ? ((info.row.original[options.idsAccessor] as unknown as number[]) ?? [])
                : [];
            return h(
                'div',
                { class: 'flex flex-wrap gap-1 items-center' },
                CONTACT_TYPE_ORDER.filter((type) => values.includes(type)).map((type) => {
                    const idx = values.indexOf(type);
                    return h(TagComponent, {
                        value: type,
                        variant: variantFor(type),
                        removable: !!options?.onRemove,
                        removeLabel: `Remove ${type} contact type`,
                        onRemove: () => options?.onRemove?.(info.row.original, type, ids[idx]),
                    });
                }),
            );
        },
        size: 300,
        enableSorting: false,
    };
}

/**
 * Creates a simple text column using TextCell component
 */
export function createTextColumn<T>(
    accessor: keyof T | string | ((row: T) => unknown),
    header: string,
    options?: ColumnOptions & { emptyText?: string; tooltip?: boolean; tooltipMaxLength?: number },
): ColumnDef<T, unknown> {
    return {
        accessorKey: typeof accessor === 'string' ? accessor : undefined,
        accessorFn: typeof accessor === 'function' ? accessor : undefined,
        header: header,
        cell: (info: CellContext<T, string | number | null | undefined>) => {
            return h(TextCell, {
                value: info.getValue(),
                emptyText: options?.emptyText,
                tooltip: options?.tooltip,
                tooltipMaxLength: options?.tooltipMaxLength,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
            cellClass: options?.className,
        },
        size: options?.size,
        minSize: options?.minSize,
        maxSize: options?.maxSize,
    };
}

/**
 * Creates a number/count column using CountCell component
 */
export function createCountColumn<T>(
    accessor: keyof T,
    header: string,
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        header: header,
        cell: (info: CellContext<T, string | number | null | undefined>) => {
            return h(CountCell, {
                value: info.getValue(),
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        size: 300,
        meta: {
            headerClass: options?.className,
            cellClass: options?.className,
        },
    };
}

/**
 * Creates a phone number column using PhoneCell component
 */
export function createPhoneColumn<T>(
    accessor: keyof T,
    header: string = 'Phone',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(PhoneCell, {
                phone: info.getValue() as string | null,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
            cellClass: options?.className,
        },
    };
}

/**
 * Creates a service level column using ServiceLevelCell component
 */
export function createServiceLevelColumn<T>(
    accessor: keyof T,
    header: string = 'Tiers',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(ServiceLevelCell, {
                level: info.getValue() as number | null,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

/**
 * Creates an organization name column using OrganizationNameCell component
 */
export function createOrganizationNameColumn<T>(
    accessor: keyof T | ((row: T) => string),
    header: string = 'Organization',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        accessorKey: typeof accessor === 'string' ? (accessor as string) : undefined,
        accessorFn: typeof accessor === 'function' ? accessor : undefined,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(OrganizationNameCell, {
                name: info.getValue() as string | null,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
            cellClass: options?.className,
        },
    };
}

/**
 * Creates a user role column using UserRoleTagComponent
 */
export function createUserRoleColumn<T>(
    accessor: keyof T,
    header: string = 'Type',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(UserRoleTagComponent, {
                orgAdmin: info.getValue() as 0 | 1,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

/**
 * Creates a security briefed status column using SecurityBriefedCell component
 */
export function createSecurityBriefedColumn<T>(
    accessor: keyof T,
    header: string = 'Security course status',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            return h(SecurityBriefedCell, {
                value: info.getValue() as 0 | 1 | boolean | null | undefined,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
            cellClass: options?.className,
        },
    };
}

/**
 * Creates a status column with colored dot indicator
 * Used for organization progress statuses
 */
export function createStatusWithDotColumn<T>(
    accessor: keyof T,
    header: string = 'Status',
    options?: ColumnOptions & {
        colorMap?: Record<string, string>;
    },
): ColumnDef<T, unknown> {
    return {
        accessorKey: accessor as string,
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            const value = info.getValue() as string;
            // Map custom status colors to TagComponent variants if possible,
            // but TagComponent already handles status detecting value.
            // For now, we'll favor the built-in status detection of TagComponent.

            return h(TagComponent, {
                value: value ?? 'Not found',
                showDot: true,
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

/**
 * Creates a full name column from separate first and last name fields
 */
export function createFullNameColumn<T>(
    firstNameAccessor: keyof T,
    lastNameAccessor: keyof T,
    header: string = 'Name',
    options?: ColumnOptions,
): ColumnDef<T, unknown> {
    return {
        id: `${String(firstNameAccessor)}_${String(lastNameAccessor)}`,
        accessorFn: (row: T) => {
            const firstName = row[firstNameAccessor] as string;
            const lastName = row[lastNameAccessor] as string;
            return `${firstName || ''} ${lastName || ''}`.trim();
        },
        header: header,
        cell: (info: CellContext<T, unknown>) => {
            const fullName = info.getValue() as string;
            return fullName || '-';
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

/**
 * Creates the Name column for user tables: avatar + full name (username when
 * the name is missing), greyed out for disabled users. The cell is inert —
 * ITDEVELOP-3397 removed the user-info popover so a click on it reaches the
 * row's row-click handler, which is where navigation to user details lives.
 */
export function createUserInfoColumn<T>(
    options?: ColumnOptions & {
        firstNameAccessor?: keyof T;
        lastNameAccessor?: keyof T;
        enabledAccessor?: keyof T;
        usernameAccessor?: keyof T;
    },
): ColumnDef<T, unknown> {
    const read = (row: T, accessor: keyof T | undefined, fallback: string) =>
        row[accessor ?? (fallback as keyof T)] as string | undefined;

    return {
        id: 'user_info',
        accessorFn: (row: T) => {
            const firstName = read(row, options?.firstNameAccessor, 'first_name');
            const lastName = read(row, options?.lastNameAccessor, 'last_name');
            return `${firstName || ''} ${lastName || ''}`.trim();
        },
        header: 'Name',
        cell: (info: CellContext<T, unknown>) => {
            const row = info.row.original;
            const firstName = read(row, options?.firstNameAccessor, 'first_name');
            const lastName = read(row, options?.lastNameAccessor, 'last_name');
            const username = read(row, options?.usernameAccessor, 'username');
            const enabled = (row[options?.enabledAccessor ?? ('enabled' as keyof T)] as boolean | undefined) ?? true;

            return h(AvatarNameCell, {
                class: enabled ? undefined : 'grayscale',
                firstName: firstName || username || '',
                lastName: firstName ? lastName || '' : '',
            });
        },
        enableSorting: options?.enableSorting ?? true,
        sortingFn: mixedSortingFn,
        meta: {
            headerClass: options?.className,
        },
    };
}

function mixedSortingFn<T>(rowA: Row<T>, rowB: Row<T>, columnId: string): number {
    // we need a custom sorting function in order to sort null and undefined properly
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);

    // Handle null/undefined - push to bottom
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    // Handle numbers
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    // Handle text (case-insensitive)
    return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
}
